import {
  AlertCircleIcon,
  CheckCircleIcon,
  CheckIcon,
  Link2Icon,
  LockIcon,
  PencilIcon,
  PlusIcon,
  ShoppingBagIcon,
  XIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Stepper } from "@/components/ui/stepper";
import { ProgressDots } from "@/components/ui/progress-dots";
import { cn } from "@/lib/utils";
import { focusRing, formatCents, formatProductSize } from "@/lib/format";
import type { StoreCartProduct, StoreMapping } from "@/lib/api";

// Re-exported here so call sites don't need to know AggregatedItem's exact origin.
export interface AggregatedItemLike {
  name: string;
  totalGrams: number;
  totalUnits: number;
  unit: string;
  category: string;
}

export interface CartSufficiencyInfo {
  status: "sufficient" | "insufficient" | "unknown";
  missingLabel: string | null;
}

export interface IngredientRowPending {
  quickAdd: boolean;
  adjust: boolean;
  qtyUpdate: boolean;
  remove: boolean;
}

interface IngredientRowProps {
  item: AggregatedItemLike;
  amountLabel: string;
  isBought: boolean;
  isConnected: boolean;
  isLocationConfigured: boolean;
  mapping: StoreMapping | null;
  cartMatch: StoreCartProduct | null;
  sufficiency: CartSufficiencyInfo | null;
  pending: IngredientRowPending;
  onToggleBought: () => void;
  onOpenPicker: () => void;
  onQuickAdd: () => void;
  onAdjust: () => void;
  onIncrement: () => void;
  onDecrement: () => void;
  onSetQuantity?: (next: number) => void;
  onRemove: () => void;
  onOpenAddressSheet: () => void;
  // Optional ref-callback so the parent can scroll the row into view.
  rowRef?: (node: HTMLLIElement | null) => void;
}

export function IngredientRow({
  item,
  amountLabel,
  isBought,
  isConnected,
  isLocationConfigured,
  mapping,
  cartMatch,
  sufficiency,
  pending,
  onToggleBought,
  onOpenPicker,
  onQuickAdd,
  onAdjust,
  onIncrement,
  onDecrement,
  onSetQuantity,
  onRemove,
  onOpenAddressSheet,
  rowRef,
}: IngredientRowProps) {
  const insufficient = sufficiency?.status === "insufficient";
  const rowState = computeRowState({ mapping, cartMatch, insufficient });
  const rowIsTappable = !isBought && isConnected && isLocationConfigured && rowState === "not-linked";
  // When connected but no address yet, a tap on the row body opens the address
  // sheet instead — same affordance as the LockIcon trailing slot.
  const rowOpensAddressSheet =
    !isBought && isConnected && !isLocationConfigured && rowState === "not-linked";

  return (
    <li ref={rowRef}>
      <article
        // Whole-row tap affordance is mouse/touch only; screen readers reach the
        // same action via the trailing slot button to avoid nesting buttons.
        // The visible trailing "Vincular" button on sm+ doubles as a keyboard
        // affordance so sighted desktop users see what tapping the row does.
        title={rowIsTappable ? "Toca para vincular" : undefined}
        className={cn(
          "group flex min-h-[56px] items-center gap-3 px-3 py-3 transition-colors",
          isBought && "bg-muted/30",
          (rowIsTappable || rowOpensAddressSheet) && "cursor-pointer hover:bg-muted/40",
        )}
        onClick={
          rowIsTappable
            ? onOpenPicker
            : rowOpensAddressSheet
              ? onOpenAddressSheet
              : undefined
        }
      >
        {/* Checkbox */}
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onToggleBought();
          }}
          aria-pressed={isBought}
          aria-label={
            isBought
              ? `Marcar ${item.name} como pendiente`
              : `Marcar ${item.name} como comprado`
          }
          className={cn(
            "grid size-7 shrink-0 place-items-center rounded-md transition-colors hover:bg-muted active:bg-muted",
            focusRing,
          )}
        >
          <span
            className={cn(
              "grid size-5 place-items-center rounded-md border-2 motion-safe:transition-all",
              isBought
                ? "border-emerald-600 bg-emerald-600 text-white"
                : "border-input bg-background",
            )}
          >
            {isBought && <CheckIcon className="size-3.5" strokeWidth={3} />}
          </span>
        </button>

        {/* Body: name and amount share a line when they fit; otherwise the
            amount wraps below, right-aligned, so a long amount ("300 g
            escurrido · ≈ 3 latas") never squeezes the name. */}
        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-x-3 gap-y-0.5">
          <p
            className={cn(
              "min-w-0 flex-auto truncate text-[15px] font-medium leading-tight motion-safe:transition-all",
              isBought && "text-muted-foreground line-through decoration-1",
            )}
          >
            {item.name}
          </p>

          <span
            className={cn(
              "text-right text-[13px] font-medium tabular-nums text-muted-foreground",
              isBought && "line-through",
            )}
          >
            {amountLabel}
          </span>
        </div>

        {/* Trailing slot — fixed 36×36pt container on mobile so the right edge
            of every row aligns regardless of state. The LinkButton on sm+ may
            expand beyond the floor (see TrailingSlot for the expanded form). */}
        <div className="grid size-9 shrink-0 place-items-center sm:size-auto sm:min-w-9">
          <TrailingSlot
            item={item}
            isBought={isBought}
            isConnected={isConnected}
            isLocationConfigured={isLocationConfigured}
            rowState={rowState}
            insufficient={insufficient}
            pending={pending}
            onOpenPicker={onOpenPicker}
            onQuickAdd={onQuickAdd}
            onOpenAddressSheet={onOpenAddressSheet}
          />
        </div>
      </article>

      {/* Product block */}
      {mapping && !cartMatch && (
        <div className="border-t border-dashed border-border bg-muted/30 px-3 py-2">
          <ProductChip
            mapping={mapping}
            isBought={isBought}
            onClick={onOpenPicker}
            itemName={item.name}
          />
        </div>
      )}

      {mapping && cartMatch && (
        <CartLineCompact
          product={cartMatch}
          tone={insufficient ? "warning" : "neutral"}
          dimmed={isBought}
          pending={pending.qtyUpdate || pending.remove}
          missingLabel={insufficient ? sufficiency?.missingLabel ?? null : null}
          adjusting={pending.adjust}
          perItemSize={formatProductSize(mapping)}
          totalSize={formatProductSize(mapping, cartMatch.quantity)}
          onChangeProduct={onOpenPicker}
          onIncrement={onIncrement}
          onDecrement={onDecrement}
          onSetQuantity={onSetQuantity}
          onRemove={onRemove}
          onAdjust={onAdjust}
        />
      )}
    </li>
  );
}

type RowState =
  | "not-linked"
  | "linked-not-in-cart"
  | "linked-in-cart-sufficient"
  | "linked-in-cart-insufficient";

function computeRowState({
  mapping,
  cartMatch,
  insufficient,
}: {
  mapping: StoreMapping | null;
  cartMatch: StoreCartProduct | null;
  insufficient: boolean;
}): RowState {
  if (!mapping) return "not-linked";
  if (!cartMatch) return "linked-not-in-cart";
  return insufficient ? "linked-in-cart-insufficient" : "linked-in-cart-sufficient";
}

interface TrailingSlotProps {
  item: AggregatedItemLike;
  isBought: boolean;
  isConnected: boolean;
  isLocationConfigured: boolean;
  rowState: RowState;
  insufficient: boolean;
  pending: IngredientRowPending;
  onOpenPicker: () => void;
  onQuickAdd: () => void;
  onOpenAddressSheet: () => void;
}

function TrailingSlot({
  item,
  isBought,
  isConnected,
  isLocationConfigured,
  rowState,
  insufficient,
  pending,
  onOpenPicker,
  onQuickAdd,
  onOpenAddressSheet,
}: TrailingSlotProps) {
  if (!isConnected) {
    return null;
  }

  // Bought items dim status indicators to neutral and remove primary actions.
  if (isBought) {
    if (rowState === "not-linked" || rowState === "linked-not-in-cart") {
      return <span className="size-2 rounded-full bg-muted-foreground/30" />;
    }
    if (rowState === "linked-in-cart-insufficient") {
      return <AlertCircleIcon className="size-5 text-muted-foreground/40" />;
    }
    return <CheckCircleIcon className="size-5 text-muted-foreground/40" />;
  }

  if (rowState === "not-linked") {
    if (!isLocationConfigured) {
      return (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onOpenAddressSheet();
          }}
          aria-label={`Configura tu dirección para vincular ${item.name}`}
          className={cn(
            "grid size-9 place-items-center rounded-md border border-dashed border-muted-foreground/30 text-muted-foreground transition-colors hover:bg-muted",
            focusRing,
          )}
        >
          <LockIcon className="size-4" />
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onOpenPicker();
        }}
        aria-label={`Vincular ${item.name} con un producto del supermercado`}
        // Mobile: icon-only 36pt square. sm+: expand into a pill with the word
        // "Vincular" so sighted desktop users see what the row-tap will do.
        className={cn(
          "inline-flex items-center justify-center gap-1.5 rounded-md border border-dashed border-border text-muted-foreground transition-colors hover:border-foreground/40 hover:bg-muted hover:text-foreground",
          "h-9 w-9 p-0 sm:w-auto sm:px-3",
          focusRing,
        )}
      >
        <Link2Icon className="size-4" />
        <span className="hidden sm:inline">Vincular</span>
      </button>
    );
  }

  if (rowState === "linked-not-in-cart") {
    if (pending.quickAdd) {
      return (
        <span className="grid size-9 place-items-center rounded-md bg-primary text-primary-foreground">
          <ProgressDots aria-label={`Agregando ${item.name}`} />
        </span>
      );
    }
    return (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onQuickAdd();
        }}
        aria-label={`Agregar ${item.name} al carrito`}
        className={cn(
          "grid size-9 place-items-center rounded-md bg-primary text-primary-foreground transition-colors hover:bg-primary/90 motion-safe:transition-transform active:scale-95",
          focusRing,
        )}
      >
        <PlusIcon className="size-4" />
      </button>
    );
  }

  if (rowState === "linked-in-cart-insufficient" || insufficient) {
    return <AlertCircleIcon className="size-5 text-amber-600 dark:text-amber-400" />;
  }

  return <CheckCircleIcon className="size-5 text-emerald-600 dark:text-emerald-400" />;
}

interface ProductChipProps {
  mapping: StoreMapping;
  isBought: boolean;
  itemName: string;
  onClick: () => void;
}

function ProductChip({ mapping, isBought, itemName, onClick }: ProductChipProps) {
  if (isBought) {
    return (
      <div className="flex min-w-0 items-center gap-2 text-[13px] text-muted-foreground/60">
        <Link2Icon className="size-3.5 shrink-0 opacity-60" />
        <span className="min-w-0 truncate">{mapping.productName}</span>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Cambiar el producto vinculado a ${itemName}`}
      className={cn(
        "group/chip flex w-full min-w-0 items-center gap-2 rounded-md py-1 text-left text-[13px] text-muted-foreground transition-colors hover:text-foreground",
        focusRing,
      )}
    >
      <Link2Icon className="size-3.5 shrink-0 opacity-60" />
      <span className="min-w-0 flex-1 truncate">{mapping.productName}</span>
      <PencilIcon className="size-3 shrink-0 opacity-0 transition-opacity group-hover/chip:opacity-50" />
    </button>
  );
}

interface CartLineCompactProps {
  product: StoreCartProduct;
  tone?: "neutral" | "warning";
  dimmed?: boolean;
  pending?: boolean;
  missingLabel?: string | null;
  adjusting?: boolean;
  withImage?: boolean;
  /** "500 g" / "30 und" — what one unit of this product contains. */
  perItemSize?: string | null;
  /** "1 kg" / "60 und" — total amount (perItemSize × quantity). */
  totalSize?: string | null;
  onChangeProduct?: () => void;
  onIncrement: () => void;
  onDecrement: () => void;
  /** Optional: set the cart line's quantity to an absolute value. Wires the
   *  Stepper's typed-input commit. */
  onSetQuantity?: (next: number) => void;
  onRemove: () => void;
  onAdjust?: () => void;
}

export function CartLineCompact({
  product,
  tone = "neutral",
  dimmed = false,
  pending = false,
  missingLabel = null,
  adjusting = false,
  withImage = true,
  perItemSize = null,
  totalSize = null,
  onChangeProduct,
  onIncrement,
  onDecrement,
  onSetQuantity,
  onRemove,
  onAdjust,
}: CartLineCompactProps) {
  const lineTotal = product.sellingPrice * product.quantity;
  const isWarning = tone === "warning";

  return (
    <div
      className={cn(
        "border-t border-dashed border-border px-3 py-2.5 motion-safe:transition-colors",
        !isWarning && "bg-muted/30",
        isWarning &&
          "border-l-4 border-l-amber-500/80 border-t-amber-200/70 bg-amber-50/70 dark:border-l-amber-500/60 dark:border-t-amber-900/50 dark:bg-amber-950/30",
      )}
    >
      {/* Row 1: thumbnail · product name · remove. Name gets the full row so
          long titles don't get clipped on phone widths. */}
      <div className="flex items-center gap-2.5">
        {withImage &&
          (product.imageUrl ? (
            <img
              src={product.imageUrl}
              alt=""
              className="size-9 shrink-0 rounded-md border border-border bg-white object-contain"
            />
          ) : (
            <span
              className="grid size-9 shrink-0 place-items-center rounded-md border border-border bg-muted/40"
              aria-hidden="true"
            >
              <ShoppingBagIcon className="size-4 text-muted-foreground/60" />
            </span>
          ))}
        {onChangeProduct ? (
          <button
            type="button"
            onClick={onChangeProduct}
            aria-label={`Cambiar producto vinculado: ${product.name}`}
            className={cn(
              "min-w-0 flex-1 break-words text-left text-[13px] font-medium leading-tight hover:underline",
              focusRing,
            )}
          >
            {product.name}
          </button>
        ) : (
          <p className="min-w-0 flex-1 break-words text-[13px] font-medium leading-tight">{product.name}</p>
        )}
        <button
          type="button"
          onClick={onRemove}
          disabled={pending}
          aria-label={`Quitar ${product.name} del carrito`}
          className={cn(
            "grid size-9 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-40",
            focusRing,
          )}
        >
          <XIcon className="size-3.5" />
        </button>
      </div>

      {/* Row 2: 3-column layout — unit price / size on the left, stepper in
          the middle, total price / total size on the right. Stays legible on
          narrow viewports because nothing competes with the product name. */}
      <div className="mt-2 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
        <div className="flex flex-col leading-tight">
          <span className="text-[13px] font-medium tabular-nums">
            {formatCents(product.sellingPrice)}
            <span className="ml-1 text-[11px] font-normal text-muted-foreground">c/u</span>
          </span>
          {perItemSize && (
            <span className="text-[11px] tabular-nums text-muted-foreground">{perItemSize}</span>
          )}
        </div>
        <Stepper
          value={product.quantity}
          pending={pending}
          minusDisabled={product.quantity <= 1}
          onDecrement={onDecrement}
          onIncrement={onIncrement}
          onCommit={onSetQuantity}
          minValue={1}
          decrementAriaLabel={`Reducir cantidad de ${product.name}`}
          incrementAriaLabel={`Aumentar cantidad de ${product.name}`}
          size="sm"
        />
        <div className="flex flex-col items-end leading-tight">
          <span className="text-[13px] font-semibold tabular-nums">
            {formatCents(lineTotal)}
          </span>
          {totalSize && (
            <span className="text-[11px] tabular-nums text-muted-foreground">{totalSize}</span>
          )}
        </div>
      </div>
      {isWarning && missingLabel && (
        <div
          role="status"
          aria-live="polite"
          className={cn(
            "mt-1.5 flex items-center justify-between gap-2 pl-[52px] text-[12px]",
            !withImage && "pl-0",
          )}
        >
          <span className="text-amber-800 dark:text-amber-300">{missingLabel}</span>
          {onAdjust && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 border-amber-300 px-2 text-[11px] text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:text-amber-200 dark:hover:bg-amber-900/40"
              onClick={onAdjust}
              disabled={adjusting}
            >
              {adjusting ? <ProgressDots aria-label="Ajustando" /> : "Ajustar"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
